import { useState, useEffect, useCallback, useMemo } from 'react'
import { apiService } from '../../../../services/api'
import { calculateParetoStats } from '../utils/chartHelpers'

/**
 * Custom hook for fetching salon analysis data
 * Handles data for all operators with unified interface
 */
export const useSalonAnalysisData = (operator) => {
  // State
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [data, setData] = useState(null)
  
  // Stock exposition state
  const [stockExpositionData, setStockExpositionData] = useState(null)
  const [stockExpositionLoading, setStockExpositionLoading] = useState(false)
  
  // Filter state
  // Default to previous year if we're in January or February (first 2 months of the year)
  const getDefaultYear = () => {
    const now = new Date()
    const currentMonth = now.getMonth() + 1 // 1-12
    const currentYear = now.getFullYear()
    return currentMonth <= 2 ? currentYear - 1 : currentYear
  }
  
  const [analysisMode, setAnalysisMode] = useState('yearly') // 'yearly', 'monthly', 'custom'
  const [selectedMonth, setSelectedMonth] = useState(null)
  const [selectedYear, setSelectedYear] = useState(getDefaultYear())
  const [startDate, setStartDate] = useState(null)
  const [endDate, setEndDate] = useState(null)
  
  // Table/filter state
  const [searchText, setSearchText] = useState('')
  const [minSales, setMinSales] = useState(0)
  const [showSpecialAttentionOnly, setShowSpecialAttentionOnly] = useState(false)
  const [salonLimit, setSalonLimit] = useState(50)

  // Get operator-specific API methods
  const getApiMethods = useCallback(() => {
    const operatorLower = operator?.toLowerCase()
    
    switch (operatorLower) {
      case 'orange':
        return {
          getSalonAnalysis: apiService.getOrangeSalonAnalysis,
          getSalonAnalysisPDF: apiService.getOrangeSalonAnalysisPDF,
          getParetoExport: apiService.getOrangeSalonParetoExport,
          getStockExpositionImpact: apiService.getOrangeSalonStockExpositionImpact
        }
      case 'plus':
        return {
          getSalonAnalysis: apiService.getPlusSalonAnalysis,
          getSalonAnalysisPDF: apiService.getPlusSalonAnalysisPDF,
          getParetoExport: apiService.getPlusSalonParetoExport,
          getStockExpositionImpact: apiService.getPlusSalonStockExpositionImpact
        }
      case 'play':
      case 'p4':
        return {
          getSalonAnalysis: apiService.getPlaySalonAnalysis,
          getSalonAnalysisPDF: apiService.getPlaySalonAnalysisPDF,
          getParetoExport: apiService.getPlaySalonParetoExport,
          getStockExpositionImpact: apiService.getPlaySalonStockExpositionImpact
        }
      case 'tmobile':
        return {
          getSalonAnalysis: apiService.getTMobileSalonAnalysis,
          getSalonAnalysisPDF: null, // T-Mobile doesn't have PDF export
          getParetoExport: apiService.getTMobileSalonParetoExport,
          getStockExpositionImpact: null // T-Mobile doesn't have stock exposition
        }
      default:
        return null
    }
  }, [operator])

  // Fetch main salon analysis data
  const fetchData = useCallback(async () => {
    const apiMethods = getApiMethods()
    if (!apiMethods?.getSalonAnalysis) {
      setError(`Nieznany operator: ${operator}`)
      return
    }

    try {
      setLoading(true)
      setError(null)
      
      const startDateStr = startDate ? startDate.toISOString().split('T')[0] : null
      const endDateStr = endDate ? endDate.toISOString().split('T')[0] : null
      
      const response = await apiMethods.getSalonAnalysis(
        9999, // limit - get all
        selectedMonth,
        selectedYear,
        startDateStr,
        endDateStr
      )
      
      setData(response.data)
    } catch (err) {
      console.error(`Error fetching ${operator} salon analysis:`, err)
      setError(`Błąd pobierania danych analizy ${operator}: ${err.message}`)
      setData(null)
    } finally {
      setLoading(false)
    }
  }, [operator, getApiMethods, selectedMonth, selectedYear, startDate, endDate])

  // Fetch stock exposition data
  const fetchStockExpositionData = useCallback(async (dataSource = 'operator', includeAllSalons = false) => {
    const apiMethods = getApiMethods()
    if (!apiMethods?.getStockExpositionImpact) {
      setStockExpositionData(null)
      return
    }

    try {
      setStockExpositionLoading(true)
      
      const startDateStr = startDate ? startDate.toISOString().split('T')[0] : null
      const endDateStr = endDate ? endDate.toISOString().split('T')[0] : null
      
      const response = await apiMethods.getStockExpositionImpact(
        selectedMonth,
        selectedYear,
        startDateStr,
        endDateStr,
        dataSource,
        includeAllSalons
      )
      
      setStockExpositionData(response.data)
    } catch (err) {
      console.error(`Error fetching ${operator} stock exposition:`, err)
      setStockExpositionData(null)
    } finally {
      setStockExpositionLoading(false)
    }
  }, [operator, getApiMethods, selectedMonth, selectedYear, startDate, endDate])

  // Effect to fetch data when filters change
  useEffect(() => {
    fetchData()
  }, [fetchData])

  // Handle analysis mode change
  const handleAnalysisModeChange = useCallback((mode) => {
    setAnalysisMode(mode)
    if (mode === 'yearly') {
      setSelectedMonth(null)
      setStartDate(null)
      setEndDate(null)
    } else if (mode === 'monthly') {
      if (!selectedMonth) setSelectedMonth(12) // Default to December
      setStartDate(null)
      setEndDate(null)
    } else if (mode === 'custom') {
      setSelectedMonth(null)
      if (!startDate) setStartDate(new Date(new Date().getFullYear(), 0, 1))
      if (!endDate) setEndDate(new Date())
    }
  }, [selectedMonth, startDate, endDate])

  // Filter salon data
  const filteredSalonData = useMemo(() => {
    if (!data?.salon_data) return []
    
    let filtered = data.salon_data.filter(salon => salon.total_sales >= minSales)
    
    if (searchText) {
      const searchLower = searchText.toLowerCase()
      filtered = filtered.filter(salon => 
        salon.salon_name?.toLowerCase().includes(searchLower) ||
        salon.city?.toLowerCase().includes(searchLower) ||
        salon.plant_code?.toLowerCase().includes(searchLower)
      )
    }
    
    if (showSpecialAttentionOnly) {
      filtered = filtered.filter(salon => salon.requires_special_attention === true)
    }
    
    return filtered
  }, [data, minSales, searchText, showSpecialAttentionOnly])

  // Calculate Pareto stats
  const paretoStats = useMemo(() => {
    return calculateParetoStats(data?.pareto_analysis)
  }, [data?.pareto_analysis])

  // Export Pareto Excel
  const exportParetoExcel = useCallback(async () => {
    const apiMethods = getApiMethods()
    if (!apiMethods?.getParetoExport) return

    try {
      const response = await apiMethods.getParetoExport(selectedMonth, selectedYear)
      
      const url = window.URL.createObjectURL(new Blob([response.data]))
      const link = document.createElement('a')
      link.href = url
      
      const monthNames = [
        'Styczen', 'Luty', 'Marzec', 'Kwiecien', 'Maj', 'Czerwiec',
        'Lipiec', 'Sierpien', 'Wrzesien', 'Pazdziernik', 'Listopad', 'Grudzien'
      ]
      const monthName = selectedMonth ? monthNames[selectedMonth - 1] : 'ROK'
      const filename = `Analiza_Pareto_${operator}_${monthName}_${selectedYear}.xlsx`
      
      link.setAttribute('download', filename)
      document.body.appendChild(link)
      link.click()
      link.remove()
      window.URL.revokeObjectURL(url)
    } catch (err) {
      console.error(`Error exporting Pareto Excel:`, err)
      setError(`Błąd exportu Excel: ${err.message}`)
    }
  }, [operator, getApiMethods, selectedMonth, selectedYear])

  // Export PDF
  const exportPDF = useCallback(async () => {
    const apiMethods = getApiMethods()
    if (!apiMethods?.getSalonAnalysisPDF || !filteredSalonData.length) return

    try {
      setLoading(true)
      
      const monthParam = analysisMode === 'monthly' ? selectedMonth : null
      const startDateParam = analysisMode === 'custom' && startDate 
        ? startDate.toISOString().split('T')[0]
        : null
      const endDateParam = analysisMode === 'custom' && endDate 
        ? endDate.toISOString().split('T')[0]
        : null
      
      const response = await apiMethods.getSalonAnalysisPDF(
        10000,
        monthParam,
        selectedYear,
        startDateParam,
        endDateParam,
        showSpecialAttentionOnly
      )
      
      const blob = new Blob([response.data], { type: 'application/pdf' })
      const url = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      
      const monthNames = [
        'Styczen', 'Luty', 'Marzec', 'Kwiecien', 'Maj', 'Czerwiec',
        'Lipiec', 'Sierpien', 'Wrzesien', 'Pazdziernik', 'Listopad', 'Grudzien'
      ]
      
      let filename
      if (analysisMode === 'monthly') {
        filename = `${operator}_salon_analysis_${monthNames[selectedMonth-1]}_${selectedYear}.pdf`
      } else if (analysisMode === 'custom') {
        filename = `${operator}_salon_analysis_${startDateParam}_do_${endDateParam}.pdf`
      } else {
        filename = `${operator}_salon_analysis_rok_${selectedYear}.pdf`
      }
      
      link.setAttribute('download', filename)
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      window.URL.revokeObjectURL(url)
    } catch (err) {
      console.error('PDF export error:', err)
      setError(`Błąd generowania PDF: ${err.response?.data?.detail || err.message}`)
    } finally {
      setLoading(false)
    }
  }, [operator, getApiMethods, analysisMode, selectedMonth, selectedYear, startDate, endDate, showSpecialAttentionOnly, filteredSalonData.length])

  // Export CSV
  const exportCSV = useCallback(() => {
    if (!filteredSalonData.length) return

    const monthNames = [
      'Styczeń', 'Luty', 'Marzec', 'Kwiecień', 'Maj', 'Czerwiec',
      'Lipiec', 'Sierpień', 'Wrzesień', 'Październik', 'Listopad', 'Grudzień'
    ]

    const headers = analysisMode === 'monthly' 
      ? ['Ranking', 'Salon', 'Miasto', 'Plant Code', 'Sprzedaż w miesiącu']
      : ['Ranking', 'Salon', 'Miasto', 'Plant Code', 'Łączna sprzedaż', ...monthNames]

    const csvData = filteredSalonData.map((salon, index) => {
      const row = [
        index + 1,
        salon.salon_name,
        salon.city,
        salon.plant_code,
        salon.total_sales
      ]
      
      if (analysisMode !== 'monthly') {
        for (let i = 1; i <= 12; i++) {
          row.push(salon.monthly_sales?.[i.toString()] || 0)
        }
      }
      
      return row
    })

    const csvContent = [headers, ...csvData]
      .map(row => row.map(field => `"${field}"`).join(','))
      .join('\n')

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
    const link = document.createElement('a')
    const url = URL.createObjectURL(blob)
    link.setAttribute('href', url)
    
    const periodSuffix = analysisMode === 'monthly' ? 'miesięczna' : 'roczna'
    const period = data?.analysis_period?.replace(/\s/g, '_') || 'data'
    link.setAttribute('download', `${operator}_salon_analysis_${periodSuffix}_${period}.csv`)
    
    link.style.visibility = 'hidden'
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
  }, [operator, analysisMode, filteredSalonData, data?.analysis_period])

  return {
    // Data
    data,
    filteredSalonData,
    paretoStats,
    stockExpositionData,
    
    // Loading states
    loading,
    stockExpositionLoading,
    error,
    
    // Filter state
    analysisMode,
    selectedMonth,
    selectedYear,
    startDate,
    endDate,
    searchText,
    minSales,
    showSpecialAttentionOnly,
    salonLimit,
    
    // Filter setters
    setAnalysisMode: handleAnalysisModeChange,
    setSelectedMonth,
    setSelectedYear,
    setStartDate,
    setEndDate,
    setSearchText,
    setMinSales,
    setShowSpecialAttentionOnly,
    setSalonLimit,
    
    // Actions
    fetchData,
    fetchStockExpositionData,
    exportParetoExcel,
    exportPDF,
    exportCSV
  }
}

export default useSalonAnalysisData
